"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AIAssistInputHandler = void 0;
const dom_helper_1 = require("./dom.helper");
const timing_helper_1 = require("./timing.helper");
const api_helper_1 = require("./api.helper");
const analysis_helper_1 = require("./analysis.helper");
class AIAssistInputHandler {
    /**
     * Bind input source and set up event listeners
     * @param input HTML textarea element
     * @param state Input handler state
     * @param context Handler context
     * @param callbacks Handler callbacks
     * @returns Updated input handler state
     */
    static bindInputSource(input, state, context, callbacks) {
        if (!context.isEnabled) {
            return state;
        }
        const onInputChange = (event) => {
            this.handleInputChange(event, state, context, callbacks);
        };
        dom_helper_1.AIAssistDOMHelper.bindInputEvents(input, onInputChange);
        return {
            ...state,
            chatInputSource: input
        };
    }
    /**
     * Handle input change events
     * @param event Keyboard event
     * @param state Input handler state
     * @param context Handler context
     * @param callbacks Handler callbacks
     */
    static handleInputChange(event, state, context, callbacks) {
        if (!context.isEnabled || context.thinking) {
            return;
        }
        const target = event.target;
        const text = target.value;
        // Clear any existing timeout
        timing_helper_1.AIAssistTimingHelper.clearTimeout(state.reactionTimeout);
        // Validate text before proceeding
        if (!api_helper_1.AIAssistApiHelper.isTextValidForAnalysis(text)) {
            return;
        }
        // Set new timeout for analysis
        state.reactionTimeout = timing_helper_1.AIAssistTimingHelper.setTimeoutWithLogging(() => {
            this.react(text, context, callbacks);
        }, 1000, 'Setting timeout for PROMPT WRITING analysis');
    }
    /**
     * React to input changes
     * @param input Input text
     * @param context Handler context
     * @param callbacks Handler callbacks
     */
    static async react(input, context, callbacks) {
        if (!context.isEnabled || context.thinking) {
            return;
        }
        callbacks.onThinkingChange(true);
        const analysisContext = {
            thinking: true,
            currentContext: context.currentContext,
            proposedKnowledgeIds: context.proposedKnowledgeIds,
            assistantSignal: context.assistantSignal,
            minimized: context.minimized,
            apiService: context.apiService
        };
        try {
            const result = await analysis_helper_1.AIAssistAnalysisHelper.react(input, analysisContext);
            // Immediately stop thinking regardless of result
            callbacks.onThinkingChange(false);
            callbacks.onKnowledgeIdsChange(result.proposedKnowledgeIds);
            callbacks.onMinimizedChange(result.minimized);
        }
        catch (error) {
            callbacks.onThinkingChange(false);
        }
    }
    /**
     * Update context from messages and trigger analysis if needed
     * @param messages Raw messages
     * @param state Input handler state
     * @param context Handler context
     * @param callbacks Handler callbacks
     * @param onContextUpdate Callback for context update
     */
    static updateContextFromMessages(messages, state, context, callbacks, onContextUpdate) {
        const convertedMessages = api_helper_1.AIAssistApiHelper.convertMessageToFrontChatMessage(messages);
        onContextUpdate(convertedMessages);
        // Only trigger analysis if we have current input and not streaming
        if (this.shouldTriggerAnalysis(state, context)) {
            this.scheduleInputAnalysis(state, context, callbacks);
        }
    }
    /**
     * Check if analysis should be triggered
     * @param state Input handler state
     * @param context Handler context
     * @returns True if analysis should be triggered
     */
    static shouldTriggerAnalysis(state, context) {
        var _a, _b, _c;
        return timing_helper_1.AIAssistTimingHelper.shouldTriggerAnalysis(context.isLLMStreaming, ((_c = (_b = (_a = state.chatInputSource) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.trim()) === null || _c === void 0 ? void 0 : _c.length) || 0);
    }
    /**
     * Schedule input analysis with timeout
     * @param state Input handler state
     * @param context Handler context
     * @param callbacks Handler callbacks
     */
    static scheduleInputAnalysis(state, context, callbacks) {
        // Clear any existing timeout
        timing_helper_1.AIAssistTimingHelper.clearTimeout(state.reactionTimeout);
        // Set a new timeout to analyze the current input with updated context
        state.reactionTimeout = timing_helper_1.AIAssistTimingHelper.setTimeoutWithLogging(() => {
            if (state.chatInputSource) {
                this.react(state.chatInputSource.value.trim(), context, callbacks);
            }
        }, 500, 'Setting timeout for input analysis (PROMPT WRITING)');
    }
    /**
     * Set LLM streaming state and handle timeout clearing
     * @param streaming Whether LLM is streaming
     * @param state Input handler state
     * @returns Updated input handler state
     */
    static setLLMStreaming(streaming, state) {
        // Clear any pending timeouts when streaming starts
        if (streaming && state.reactionTimeout) {
            timing_helper_1.AIAssistTimingHelper.clearTimeout(state.reactionTimeout);
            return {
                ...state,
                reactionTimeout: null
            };
        }
        return state;
    }
    /**
     * Clear timeout
     * @param state Input handler state
     * @returns Updated input handler state
     */
    static clearTimeout(state) {
        if (state.reactionTimeout) {
            timing_helper_1.AIAssistTimingHelper.clearTimeout(state.reactionTimeout);
            return {
                ...state,
                reactionTimeout: null
            };
        }
        return state;
    }
    /**
     * Analyze current context
     * @param context Handler context
     * @param callbacks Handler callbacks
     */
    static async analyzeCurrentContext(context, callbacks) {
        if (!context.isEnabled || context.thinking || context.currentContext.length === 0) {
            return;
        }
        callbacks.onThinkingChange(true);
        const analysisContext = {
            thinking: true,
            currentContext: context.currentContext,
            proposedKnowledgeIds: context.proposedKnowledgeIds,
            assistantSignal: context.assistantSignal,
            minimized: context.minimized,
            apiService: context.apiService
        };
        try {
            const result = await analysis_helper_1.AIAssistAnalysisHelper.analyzeCurrentContext(analysisContext);
            // Immediately stop thinking regardless of result
            callbacks.onThinkingChange(false);
            callbacks.onKnowledgeIdsChange(result.proposedKnowledgeIds);
            callbacks.onMinimizedChange(result.minimized);
        }
        catch (error) {
            callbacks.onThinkingChange(false);
        }
    }
    /**
     * Analyze after LLM response
     * @param context Handler context
     * @param callbacks Handler callbacks
     */
    static async analyzeAfterLLMResponse(context, callbacks) {
        if (!context.isEnabled || context.thinking || context.currentContext.length === 0) {
            return;
        }
        callbacks.onThinkingChange(true);
        const analysisContext = {
            thinking: true,
            currentContext: context.currentContext,
            proposedKnowledgeIds: context.proposedKnowledgeIds,
            assistantSignal: context.assistantSignal,
            minimized: context.minimized,
            apiService: context.apiService
        };
        try {
            const result = await analysis_helper_1.AIAssistAnalysisHelper.analyzeAfterLLMResponse(analysisContext);
            // Immediately stop thinking regardless of result
            callbacks.onThinkingChange(false);
            callbacks.onKnowledgeIdsChange(result.proposedKnowledgeIds);
            callbacks.onMinimizedChange(result.minimized);
        }
        catch (error) {
            callbacks.onThinkingChange(false);
        }
    }
    /**
     * Create initial input handler state
     * @returns Initial state
     */
    static createInitialState() {
        return {
            chatInputSource: null,
            reactionTimeout: null
        };
    }
    /**
     * Get input source value
     * @param state Input handler state
     * @returns Current input value or empty string
     */
    static getInputValue(state) {
        var _a, _b;
        return ((_b = (_a = state.chatInputSource) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.trim()) || '';
    }
    /**
     * Check if input source is available
     * @param state Input handler state
     * @returns True if input source is available
     */
    static hasInputSource(state) {
        return !!state.chatInputSource;
    }
    /**
     * Focus input source
     * @param state Input handler state
     */
    static focusInput(state) {
        if (state.chatInputSource) {
            state.chatInputSource.focus();
        }
    }
    /**
     * Set input source value
     * @param state Input handler state
     * @param value Value to set
     * @param triggerEvent Whether to trigger input event
     */
    static setInputValue(state, value, triggerEvent = true) {
        if (state.chatInputSource) {
            state.chatInputSource.value = value;
            if (triggerEvent) {
                state.chatInputSource.dispatchEvent(new Event('input', { bubbles: true }));
            }
        }
    }
}
exports.AIAssistInputHandler = AIAssistInputHandler;
